import { useMemo, useState } from 'react';
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useAccount } from 'wagmi';
import type { Timeframe } from '../ai/signals';
import type { MarketPair } from '../data/mockMarket';
import { TARGET_CHAIN } from '../web3/config';
import { getChainlinkFeed } from '../web3/priceFeeds';
import { useChainlinkHistory } from '../web3/useChainlinkHistory';
import { useLiveMarket } from '../web3/useLiveMarket';
import { useChainlinkPrice } from '../web3/useChainlinkPrice';

const TIMEFRAMES: Timeframe[] = ['1h', '4h', '1d'];

function formatPrice(value: number) {
  return value.toLocaleString('en-US', { maximumFractionDigits: value < 1 ? 6 : 2 });
}

export default function PriceChart({ pair }: { pair: MarketPair }) {
  const [timeframe, setTimeframe] = useState<Timeframe>('4h');
  const chainId = useAccount().chainId ?? TARGET_CHAIN.id;
  const { price, change24h, high24h, low24h, volume24h, liquidityUsd, series } = useLiveMarket(pair);
  const chainlinkPrice = useChainlinkPrice(pair.base);
  const history = useChainlinkHistory(pair.base, timeframe);
  const hasFeed = Boolean(getChainlinkFeed(pair.base, chainId));
  const isUp = change24h >= 0;
  const color = isUp ? '#22d3a8' : '#f5484f';

  // real on-chain price samples accumulate over time (see priceHistory.ts) — this
  // just makes sure there's always at least a flat line instead of an empty chart
  // when the pool hasn't been polled long enough yet to have much history.
  const data = useMemo(() => {
    if (hasFeed && history?.length) {
      const points = [...history];
      const lastPrice = points[points.length - 1].price;
      const materialDifference =
        Math.abs((price ?? lastPrice) - lastPrice) >
        Math.max(Math.abs(lastPrice) * 1e-6, Number.EPSILON);
      if (price !== undefined && materialDifference) points.push({ t: Date.now(), price });
      return points;
    }

    if (price === undefined) return [];
    if (series.length === 0) return [{ t: Date.now(), price }];
    return [...series, { t: Date.now(), price }];
  }, [hasFeed, history, series, price]);

  return (
    <div className="flex-1 flex flex-col p-4 border-b border-abyss-700">
      <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 mb-4">
        <div className="flex items-center gap-2">
          <span className="text-lg font-semibold text-foam-100">
            {pair.base}/{pair.quote}
          </span>
          <span
            className={`text-xs font-semibold px-1.5 py-0.5 rounded ${
              isUp ? 'text-tide-400 bg-tide-400/10' : 'text-coral-400 bg-coral-400/10'
            }`}
          >
            {isUp ? '+' : ''}
            {change24h.toFixed(2)}%
          </span>
          {chainlinkPrice !== undefined && (
            <span
              className="text-xs font-semibold px-1.5 py-0.5 rounded text-sky-400 bg-sky-400/10"
              title="Chainlink 오라클이 제공하는 실제 시장 가격 — AMM 풀 가격과는 별개입니다"
            >
              실시간 시세 ${formatPrice(chainlinkPrice)}
            </span>
          )}
        </div>
        <Stat label="24h High" value={high24h} />
        <Stat label="24h Low" value={low24h} />
        <Stat label="24h Volume" value={volume24h} prefix="$" compact />
        <Stat label="Liquidity" value={liquidityUsd} prefix="$" compact />
        {hasFeed && (
          <div className="flex items-center gap-1 ml-auto">
            {TIMEFRAMES.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setTimeframe(option)}
                className={`px-2 py-1 rounded-full text-xs font-semibold transition-colors ${
                  timeframe === option
                    ? 'text-tide-400 bg-tide-400/10'
                    : 'text-foam-500 hover:text-foam-300'
                }`}
              >
                {option}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex-1 min-h-[220px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id="priceFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis dataKey="t" hide />
            <YAxis domain={['auto', 'auto']} hide />
            <Tooltip
              contentStyle={{
                background: '#0d1420',
                border: '1px solid #182437',
                borderRadius: 8,
                fontSize: 12,
              }}
              labelFormatter={() => ''}
              formatter={(value) => [formatPrice(Number(value)), 'Price']}
            />
            <Area
              type="monotone"
              dataKey="price"
              stroke={color}
              strokeWidth={2}
              fill="url(#priceFill)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  prefix = '',
  compact = false,
}: {
  label: string;
  value: number | undefined;
  prefix?: string;
  compact?: boolean;
}) {
  const formatted =
    value === undefined
      ? '-'
      : compact
        ? Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 2 }).format(value)
        : formatPrice(value);

  return (
    <div className="text-xs">
      <div className="text-foam-500">{label}</div>
      <div className="text-foam-100 tabular-nums font-medium">
        {value !== undefined && prefix}
        {formatted}
      </div>
    </div>
  );
}
